/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import once from 'lodash.once';
import { configure } from 'mobx';
import { RootStore } from 'src/core/stores/RootStore';
import { UIConfigsStore } from 'src/core/stores/UIConfigsStore';
import { GlobalState } from 'src/core/types/GlobalState';
import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { httpClient } from 'src/core/utils/api/httpClient';
import { createDispatcher } from 'src/core/utils/createDispatcher';
import { createErrorInterceptor } from 'src/core/utils/interceptors/errorInterceptor';
import { createRateLimiterInterceptor } from 'src/core/utils/interceptors/rateLimiterInterceptor';
import { createRetryInterceptor } from 'src/core/utils/interceptors/retryInterceptor';
import { createSessionExpiryInterceptor } from 'src/core/utils/interceptors/sessionExpiryInterceptor';
import { getGuid } from 'src/core/utils/randomValueGenerators';
import { setUserSessionId } from 'src/core/utils/userSessionId';

/**
 * Sets up a common interceptors pipeline for
 * the HTTP client.
 */
const setupHttpClientInterceptors = () => {
    createRateLimiterInterceptor(httpClient);
    createRetryInterceptor(httpClient);
    createErrorInterceptor(httpClient);
    createSessionExpiryInterceptor(httpClient);
};

if (isDevelopmentEnvironment()) {
    configure({ enforceActions: 'observed' });
}

/**
 * Create the global application state constructs. The usage
 * of `lodash.once` ensures that the generated object from this
 * factory is a singleton.
 */
export const bootstrapGlobalState: () => GlobalState = once(() => {
    const rootStore = new RootStore();
    const uiStore = new UIConfigsStore();

    setUserSessionId(getGuid());
    setupHttpClientInterceptors();

    return {
        uiStore,
        rootStore,
        dispatcher: createDispatcher({ rootStore, uiStore, lifetime: null })
    };
});
